/**
 * Shared types and interfaces for the MCDC Convention Voting System
 */
/**
 * Pool type enum
 * Categorizes pools by their intended purpose
 *
 * Database enum (pool_type):
 * - 'voter': Pool for quorum counting and voting
 * - 'watcher': Pool for observers who can view but not vote
 * - 'meeting_admin': Pool for meeting administrators
 *
 * NULL/not specified: Legacy or general-purpose pools
 *
 * IMPORTANT: Keep this enum in sync with database migrations
 */
export var PoolType;
(function (PoolType) {
    PoolType["Voter"] = "voter";
    PoolType["Watcher"] = "watcher";
    PoolType["MeetingAdmin"] = "meeting_admin";
})(PoolType || (PoolType = {}));
/**
 * Meeting Management Types
 */
/**
 * Motion status enum
 * Status can only advance forward: not_yet_started -> voting_active -> voting_complete
 *
 * Database enum (motion_status):
 * - 'not_yet_started': Voting has not begun (default)
 * - 'voting_active': Voting is currently in progress
 * - 'voting_complete': Voting has ended
 *
 * IMPORTANT: Keep this enum in sync with database migrations
 */
export var MotionStatus;
(function (MotionStatus) {
    MotionStatus["NotYetStarted"] = "not_yet_started";
    MotionStatus["VotingActive"] = "voting_active";
    MotionStatus["VotingComplete"] = "voting_complete";
})(MotionStatus || (MotionStatus = {}));
/**
 * Participant role enum
 * Represents the role of a user when they join a meeting
 *
 * Database enum (participant_role):
 * - 'voter': Can vote on motions and counts toward quorum
 * - 'watcher': Can observe meeting but cannot vote
 * - 'meeting_admin': Can administer the meeting (scoped admin rights)
 *
 * IMPORTANT: Keep this enum in sync with database migrations
 */
export var ParticipantRole;
(function (ParticipantRole) {
    ParticipantRole["Voter"] = "voter";
    ParticipantRole["Watcher"] = "watcher";
    ParticipantRole["MeetingAdmin"] = "meeting_admin";
})(ParticipantRole || (ParticipantRole = {}));
/**
 * Login error codes for specific failure states
 */
export var LoginErrorCode;
(function (LoginErrorCode) {
    LoginErrorCode["InvalidCredentials"] = "INVALID_CREDENTIALS";
    LoginErrorCode["AccountDisabled"] = "ACCOUNT_DISABLED";
    LoginErrorCode["NoPasswordSet"] = "NO_PASSWORD_SET";
    LoginErrorCode["LoginDisabled"] = "LOGIN_DISABLED";
    LoginErrorCode["RateLimited"] = "RATE_LIMITED";
})(LoginErrorCode || (LoginErrorCode = {}));
/**
 * Meeting Document Types
 */
/**
 * Document category enum
 * Categories for organizing meeting documents
 *
 * Database enum (document_category):
 * - 'invitation': Meeting invitation letter
 * - 'agenda': Meeting agenda
 * - 'reports': Reports and summaries
 * - 'previous_meeting_issues': Issues from previous meetings
 * - 'proposals': Proposals to be discussed
 * - 'rules': Rules and procedures
 * - 'user_guide': System user guide (system-wide, not meeting-specific)
 *
 * IMPORTANT: Keep this enum in sync with database migrations
 */
export var DocumentCategory;
(function (DocumentCategory) {
    DocumentCategory["Invitation"] = "invitation";
    DocumentCategory["Agenda"] = "agenda";
    DocumentCategory["Reports"] = "reports";
    DocumentCategory["PreviousMeetingIssues"] = "previous_meeting_issues";
    DocumentCategory["Proposals"] = "proposals";
    DocumentCategory["Rules"] = "rules";
    DocumentCategory["UserGuide"] = "user_guide";
})(DocumentCategory || (DocumentCategory = {}));
/**
 * Human-readable labels for document categories
 */
export const DOCUMENT_CATEGORY_LABELS = {
    [DocumentCategory.Invitation]: "Invitation",
    [DocumentCategory.Agenda]: "Agenda",
    [DocumentCategory.Reports]: "Reports",
    [DocumentCategory.PreviousMeetingIssues]: "Previous Meeting Minutes",
    [DocumentCategory.Proposals]: "Proposals",
    [DocumentCategory.Rules]: "Rules",
    [DocumentCategory.UserGuide]: "User Guide",
};
/**
 * Service Error Types
 * Used for structured error handling with proper HTTP status code mapping
 */
/**
 * Structured error codes for service-layer errors.
 * Used to determine HTTP status codes without brittle string matching.
 *
 * HTTP Status Code Mapping:
 * - UNAUTHORIZED → 401
 * - FORBIDDEN, NOT_ELIGIBLE_* → 403
 * - NOT_FOUND, *_NOT_FOUND, MEETING_NOT_ACTIVE → 404
 * - ALREADY_VOTED, VOTING_CLOSED, ALREADY_IN_MEETING, NOT_IN_MEETING → 409
 * - INVALID_* → 400
 * - INTERNAL_ERROR → 500
 */
export var ServiceErrorCode;
(function (ServiceErrorCode) {
    // Authentication & Authorization
    ServiceErrorCode["UNAUTHORIZED"] = "UNAUTHORIZED";
    ServiceErrorCode["FORBIDDEN"] = "FORBIDDEN";
    // Resource not found
    ServiceErrorCode["NOT_FOUND"] = "NOT_FOUND";
    ServiceErrorCode["MOTION_NOT_FOUND"] = "MOTION_NOT_FOUND";
    ServiceErrorCode["MEETING_NOT_FOUND"] = "MEETING_NOT_FOUND";
    ServiceErrorCode["USER_NOT_FOUND"] = "USER_NOT_FOUND";
    ServiceErrorCode["POOL_NOT_FOUND"] = "POOL_NOT_FOUND";
    ServiceErrorCode["CHOICE_NOT_FOUND"] = "CHOICE_NOT_FOUND";
    // Conflict/state errors
    ServiceErrorCode["ALREADY_VOTED"] = "ALREADY_VOTED";
    ServiceErrorCode["VOTING_CLOSED"] = "VOTING_CLOSED";
    ServiceErrorCode["MEETING_NOT_ACTIVE"] = "MEETING_NOT_ACTIVE";
    ServiceErrorCode["ALREADY_IN_MEETING"] = "ALREADY_IN_MEETING";
    ServiceErrorCode["NOT_IN_MEETING"] = "NOT_IN_MEETING";
    // Eligibility
    ServiceErrorCode["NOT_ELIGIBLE_FOR_MOTION"] = "NOT_ELIGIBLE_FOR_MOTION";
    ServiceErrorCode["NOT_ELIGIBLE_FOR_MEETING"] = "NOT_ELIGIBLE_FOR_MEETING";
    // Validation errors
    ServiceErrorCode["INVALID_CHOICE"] = "INVALID_CHOICE";
    ServiceErrorCode["INVALID_SELECTION_COUNT"] = "INVALID_SELECTION_COUNT";
    ServiceErrorCode["INVALID_INPUT"] = "INVALID_INPUT";
    // Generic
    ServiceErrorCode["INTERNAL_ERROR"] = "INTERNAL_ERROR";
})(ServiceErrorCode || (ServiceErrorCode = {}));
/**
 * Projector Display Types
 * Used for admin projector control and display functionality
 */
/**
 * Projector display modes
 * Each mode determines what content is shown on the projector display
 */
export var ProjectorDisplayMode;
(function (ProjectorDisplayMode) {
    /** Blank/standby screen */
    ProjectorDisplayMode["Blank"] = "blank";
    /** Meeting title with optional artwork/logo */
    ProjectorDisplayMode["MeetingTitle"] = "meeting_title";
    /** Live quorum statistics */
    ProjectorDisplayMode["Quorum"] = "quorum";
    /** Current motion with choices */
    ProjectorDisplayMode["Motion"] = "motion";
    /** Live voting progress (no individual votes) */
    ProjectorDisplayMode["VotingActive"] = "voting_active";
    /** Final voting results */
    ProjectorDisplayMode["Results"] = "results";
    /** QR code linking to user manual PDF */
    ProjectorDisplayMode["QRUserGuide"] = "qr_user_guide";
    /** QR code linking to organization website */
    ProjectorDisplayMode["QROrganization"] = "qr_organization";
    /** Admin-entered custom text message */
    ProjectorDisplayMode["CustomMessage"] = "custom_message";
})(ProjectorDisplayMode || (ProjectorDisplayMode = {}));
/**
 * Font size options for custom messages
 */
export var ProjectorFontSize;
(function (ProjectorFontSize) {
    ProjectorFontSize["Small"] = "small";
    ProjectorFontSize["Medium"] = "medium";
    ProjectorFontSize["Large"] = "large";
})(ProjectorFontSize || (ProjectorFontSize = {}));
/**
 * Font family options for custom messages
 */
export var ProjectorFontFamily;
(function (ProjectorFontFamily) {
    /** Default sans-serif system font */
    ProjectorFontFamily["SansSerif"] = "sans-serif";
    /** Classic serif font */
    ProjectorFontFamily["Serif"] = "serif";
    /** Monospace/typewriter style */
    ProjectorFontFamily["Monospace"] = "monospace";
    /** Elegant script/cursive style */
    ProjectorFontFamily["Cursive"] = "cursive";
    /** Bold display style */
    ProjectorFontFamily["Display"] = "display";
})(ProjectorFontFamily || (ProjectorFontFamily = {}));
//# sourceMappingURL=types.js.map